import Phaser from 'phaser';
import { SCENES, DESIGN_WIDTH, DESIGN_HEIGHT, COLORS } from '../constants';
import { SceneRouter } from '../systems/SceneRouter';

export class PreloadScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENES.PRELOAD });
  }

  preload(): void {
    // Create loading bar
    const progressBar = this.add.graphics();
    const progressBox = this.add.graphics();
    progressBox.fillStyle(COLORS.BG_MEDIUM, 0.8);
    progressBox.fillRect(DESIGN_WIDTH / 2 - 160, DESIGN_HEIGHT / 2 - 25, 320, 50);

    const loadingText = this.add.text(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2 - 50, 'Loading...', {
      fontSize: '24px',
      color: '#ffffff',
    });
    loadingText.setOrigin(0.5);

    const percentText = this.add.text(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2, '0%', {
      fontSize: '18px',
      color: '#ffffff',
    });
    percentText.setOrigin(0.5);

    // Progress events
    this.load.on('progress', (value: number) => {
      percentText.setText(Math.round(value * 100) + '%');
      progressBar.clear();
      progressBar.fillStyle(COLORS.PRIMARY, 1);
      progressBar.fillRect(DESIGN_WIDTH / 2 - 150, DESIGN_HEIGHT / 2 - 15, 300 * value, 30);
    });

    this.load.on('complete', () => {
      progressBar.destroy();
      progressBox.destroy();
      loadingText.destroy();
      percentText.destroy();
    });

    // Load tree images (9 s1 variants - forest edge)
    this.load.image('tree-1', 'assets/trees/tree-s1-01.png');
    this.load.image('tree-2', 'assets/trees/tree-s1-02.png');
    this.load.image('tree-3', 'assets/trees/tree-s1-03.png');
    this.load.image('tree-4', 'assets/trees/tree-s1-04.png');
    this.load.image('tree-5', 'assets/trees/tree-s1-05.png');
    this.load.image('tree-6', 'assets/trees/tree-s1-06.png');
    this.load.image('tree-7', 'assets/trees/tree-s1-07.png');
    this.load.image('tree-8', 'assets/trees/tree-s1-08.png');
    this.load.image('tree-9', 'assets/trees/tree-s1-09.png');

    // Load bog trees (8 b1 variants - swamp)
    for (let i = 1; i <= 8; i++) {
      const n = i < 10 ? `0${i}` : `${i}`;
      this.load.image(`tree-bog-${i}`, `assets/trees/tree-b1-${n}.png`);
    }

    // Load ruins/highlands trees (10 r1 variants)
    for (let i = 1; i <= 10; i++) {
      const n = i < 10 ? `0${i}` : `${i}`;
      this.load.image(`tree-ruins-${i}`, `assets/trees/tree-r1-${n}.png`);
    }

    // Load volcano trees (12 v1 variants)
    for (let i = 1; i <= 12; i++) {
      const n = i < 10 ? `0${i}` : `${i}`;
      this.load.image(`tree-volcano-${i}`, `assets/trees/tree-v1-${n}.png`);
    }

    // Load grass backgrounds
    this.load.image('grass', 'assets/grass/grass-s01-01.jpg');
    this.load.image('grass-bog', 'assets/grass/bog-s01-01.jpg');
    this.load.image('grass-ruins', 'assets/grass/ruins-s01-01.jpg');
    this.load.image('grass-volcano', 'assets/grass/volcano-s01-01.jpg');
    this.load.image('grass-path', 'assets/grass/grass-s02-01.jpg');
    this.load.image('bog-path', 'assets/grass/bog-s02-01.jpg');
    this.load.image('ruins-path', 'assets/grass/ruins-s02-01.jpg');
    this.load.image('volcano-path', 'assets/grass/volcano-s02-01.jpg');

    // Load village center (base)
    this.load.image('village-center', 'assets/village-center.png');

    // Load villager characters in front of hut (b1, b2 standing)
    this.load.image('villager-b1', 'assets/characters/b1-standing-01.png');
    this.load.image('villager-b2', 'assets/characters/b2-standing-01.png');

    // Load logo (main menu)
    this.load.image('logo', 'assets/logo.png');

    // Load menu background (main menu and map menu)
    this.load.image('menu-background', 'assets/menu-background.jpg');

    // Load tower images (type-NN: arrow 01=right through 08; 8 frames for rotation)
    this.load.image('tower-arrow', 'assets/towers/arrow-01.png');
    this.load.image('tower-arrow-01', 'assets/towers/arrow-01.png');
    this.load.image('tower-arrow-02', 'assets/towers/arrow-02.png');
    this.load.image('tower-arrow-03', 'assets/towers/arrow-03.png');
    this.load.image('tower-arrow-04', 'assets/towers/arrow-04.png');
    this.load.image('tower-arrow-05', 'assets/towers/arrow-05.png');
    this.load.image('tower-arrow-06', 'assets/towers/arrow-06.png');
    this.load.image('tower-arrow-07', 'assets/towers/arrow-07.png');
    this.load.image('tower-arrow-08', 'assets/towers/arrow-08.png');
    this.load.image('tower-cannon', 'assets/towers/cannon-01.png');
    this.load.image('tower-cannon-01', 'assets/towers/cannon-01.png');
    this.load.image('tower-cannon-02', 'assets/towers/cannon-02.png');
    this.load.image('tower-cannon-03', 'assets/towers/cannon-03.png');
    this.load.image('tower-cannon-04', 'assets/towers/cannon-04.png');
    this.load.image('tower-cannon-05', 'assets/towers/cannon-05.png');
    this.load.image('tower-cannon-06', 'assets/towers/cannon-06.png');
    this.load.image('tower-cannon-07', 'assets/towers/cannon-07.png');
    this.load.image('tower-cannon-08', 'assets/towers/cannon-08.png');
    this.load.image('tower-antiair', 'assets/towers/anti-air-01.png');
    this.load.image('tower-antiair-01', 'assets/towers/anti-air-01.png');
    this.load.image('tower-antiair-02', 'assets/towers/anti-air-02.png');
    this.load.image('tower-antiair-03', 'assets/towers/anti-air-03.png');
    this.load.image('tower-antiair-04', 'assets/towers/anti-air-04.png');
    this.load.image('tower-antiair-05', 'assets/towers/anti-air-05.png');
    this.load.image('tower-antiair-06', 'assets/towers/anti-air-06.png');
    this.load.image('tower-antiair-07', 'assets/towers/anti-air-07.png');
    this.load.image('tower-antiair-08', 'assets/towers/anti-air-08.png');
    this.load.image('tower-tesla', 'assets/towers/tesla-01.png');
    this.load.image('tower-tesla-01', 'assets/towers/tesla-01.png');
    this.load.image('tower-tesla-02', 'assets/towers/tesla-02.png');
    this.load.image('tower-tesla-03', 'assets/towers/tesla-03.png');
    this.load.image('tower-tesla-04', 'assets/towers/tesla-04.png');
    this.load.image('tower-tesla-05', 'assets/towers/tesla-05.png');
    this.load.image('tower-tesla-06', 'assets/towers/tesla-06.png');
    this.load.image('tower-fire', 'assets/towers/fire-01.png');
    this.load.image('tower-fire-01', 'assets/towers/fire-01.png');
    this.load.image('tower-fire-02', 'assets/towers/fire-02.png');
    this.load.image('tower-fire-03', 'assets/towers/fire-03.png');
    this.load.image('tower-fire-04', 'assets/towers/fire-04.png');
    this.load.image('tower-fire-05', 'assets/towers/fire-05.png');
    this.load.image('tower-fire-06', 'assets/towers/fire-06.png');
    this.load.image('tower-fire-07', 'assets/towers/fire-07.png');
    this.load.image('tower-fire-08', 'assets/towers/fire-08.png');
    this.load.image('tower-ice', 'assets/towers/freeze-01.png');
    this.load.image('tower-laser', 'assets/towers/laser-01.png');
    this.load.image('tower-laser-01', 'assets/towers/laser-01.png');
    this.load.image('tower-laser-02', 'assets/towers/laser-02.png');
    this.load.image('tower-laser-03', 'assets/towers/laser-03.png');
    this.load.image('tower-laser-04', 'assets/towers/laser-04.png');
    this.load.image('tower-laser-05', 'assets/towers/laser-05.png');
    this.load.image('tower-laser-06', 'assets/towers/laser-06.png');
    this.load.image('tower-laser-07', 'assets/towers/laser-07.png');
    this.load.image('tower-laser-08', 'assets/towers/laser-08.png');
    this.load.image('tower-mortar', 'assets/towers/mortar-01.png');
    this.load.image('tower-mortar-01', 'assets/towers/mortar-01.png');
    this.load.image('tower-mortar-02', 'assets/towers/mortar-02.png');
    this.load.image('tower-mortar-03', 'assets/towers/mortar-03.png');
    this.load.image('tower-mortar-04', 'assets/towers/mortar-04.png');
    this.load.image('tower-mortar-05', 'assets/towers/mortar-05.png');
    this.load.image('tower-mortar-06', 'assets/towers/mortar-06.png');
    this.load.image('tower-mortar-07', 'assets/towers/mortar-07.png');
    this.load.image('tower-mortar-08', 'assets/towers/mortar-08.png');
    this.load.image('tower-hive', 'assets/towers/hive-01.png');
    this.load.image('tower-lightning', 'assets/towers/lightning-01.png');
    this.load.image('tower-trap', 'assets/towers/trap-01.png');
    this.load.image('tower-gem', 'assets/towers/gem-01.png');

    // Load construction images (4 stages for building progress)
    this.load.image('construction-1', 'assets/towers/construction-01.png');
    this.load.image('construction-2', 'assets/towers/construction-02.png');
    this.load.image('construction-3', 'assets/towers/construction-03.png');
    this.load.image('construction-4', 'assets/towers/construction-04.png');

    // Load tower icons (for selector UI and placement preview)
    this.load.image('icon-arrow', 'assets/icons/arrow-01.png');
    this.load.image('icon-cannon', 'assets/icons/cannon-01.png');
    this.load.image('icon-antiair', 'assets/icons/anti-air-01.png');
    this.load.image('icon-tesla', 'assets/icons/tesla-01.png');
    this.load.image('icon-fire', 'assets/icons/fire-01.png');
    this.load.image('icon-ice', 'assets/icons/freeze-01.png');
    this.load.image('icon-laser', 'assets/icons/laser-01.png');
    this.load.image('icon-mortar', 'assets/icons/mortar-01.png');
    this.load.image('icon-hive', 'assets/icons/hive-01.png');
    this.load.image('icon-lightning', 'assets/icons/lightning-01.png');
    this.load.image('icon-gem', 'assets/icons/gem-01.png');
    this.load.image('icon-trap', 'assets/icons/trap-01.png');

    // Load player character images (all 4 players, 4 run frames + 4 dance frames each)
    for (let p = 1; p <= 4; p++) {
      this.load.image(`player-${p}-standing`, `assets/characters/p${p}-standing-01.png`);
      this.load.image(`player-${p}-run-1`, `assets/characters/p${p}-running-01.png`);
      this.load.image(`player-${p}-run-2`, `assets/characters/p${p}-running-02.png`);
      this.load.image(`player-${p}-run-3`, `assets/characters/p${p}-running-03.png`);
      this.load.image(`player-${p}-run-4`, `assets/characters/p${p}-running-04.png`);
      this.load.image(`player-${p}-dance-1`, `assets/characters/p${p}-dancing-01.png`);
      this.load.image(`player-${p}-dance-2`, `assets/characters/p${p}-dancing-02.png`);
      this.load.image(`player-${p}-dance-3`, `assets/characters/p${p}-dancing-03.png`);
      this.load.image(`player-${p}-dance-4`, `assets/characters/p${p}-dancing-04.png`);
    }

    // Load currency images
    this.load.image('coin-1', 'assets/currency/coin-01.png');
    this.load.image('coin-2', 'assets/currency/coin-02.png');
    this.load.image('coin-3', 'assets/currency/coin-03.png');
    this.load.image('coin-4', 'assets/currency/coin-04.png');
    this.load.image('gem-1', 'assets/currency/gem-01.png');

    // Load generic in-game music tracks (BPM in filename for dance sync)
    this.load.audio('bgm-1', 'assets/music/generic/mon-cover-01-056bpm.wav');
    this.load.audio('bgm-2', 'assets/music/generic/mon-cover-02-084bpm.wav');
    this.load.audio('bgm-3', 'assets/music/generic/mon-cover-03-056bpm.wav');
    this.load.audio('bgm-4', 'assets/music/generic/mon-cover-04-095bpm.mp3');
    this.load.audio('bgm-5', 'assets/music/generic/mon-cover-05-95bpm.mp3');
    this.load.audio('bgm-6', 'assets/music/generic/mon-cover-06-088bpm.mp3');
    this.load.audio('bgm-7', 'assets/music/generic/mon-cover-07-094bpm.wav');

    // Load menu music (BPM in filenames for dance sync)
    this.load.audio('menu-music-1', 'assets/music/menu/menu-01-088bpm.wav');
    this.load.audio('menu-music-2', 'assets/music/menu/menu-02-090bpm.wav');

    // Load SFX
    this.load.audio('sfx-arrow', 'assets/sfx/arrow.wav');
    this.load.audio('sfx-cannon', 'assets/sfx/canon.wav');
    this.load.audio('sfx-antiair', 'assets/sfx/anti-air.wav');
    this.load.audio('sfx-antiair-2', 'assets/sfx/anti-air-2.wav');
    this.load.audio('sfx-tesla', 'assets/sfx/tesla.wav');
    this.load.audio('sfx-fire', 'assets/sfx/fire.wav');
    this.load.audio('sfx-ice', 'assets/sfx/ice.wav');
    this.load.audio('sfx-laser', 'assets/sfx/laser.wav');
    this.load.audio('sfx-mortar', 'assets/sfx/mortar.wav');
    this.load.audio('sfx-dying', 'assets/sfx/dying.wav');
    this.load.audio('sfx-hit-bad', 'assets/sfx/hit-bad.wav');
    this.load.audio('sfx-scream-01', 'assets/sfx/scream-01.wav');
    this.load.audio('sfx-scream-02', 'assets/sfx/scream-02.wav');
    this.load.audio('sfx-scream-03', 'assets/sfx/scream-03.wav');
    this.load.audio('sfx-scream-04', 'assets/sfx/scream-04.wav');
    this.load.audio('sfx-scream-05', 'assets/sfx/scream-05.wav');
    this.load.audio('sfx-explosion', 'assets/sfx/explosion.wav');
    this.load.audio('sfx-collecting', 'assets/sfx/collecting-coins.wav');
    this.load.audio('sfx-construction', 'assets/sfx/construction.wav');
    this.load.audio('victory', 'assets/sfx/victory.wav');
    this.load.audio('defeat', 'assets/sfx/defeat.wav');

    // Load monster images (running animation frames; 200ms base between frames)
    this.load.image('monster-crawler-1', 'assets/monsters/crawler-01.png');
    this.load.image('monster-crawler-2', 'assets/monsters/crawler-02.png');
    this.load.image('monster-runner-1', 'assets/monsters/runner-01.png');
    this.load.image('monster-runner-2', 'assets/monsters/runner-02.png');
    this.load.image('monster-brute-1', 'assets/monsters/brute-01.png');
    this.load.image('monster-brute-2', 'assets/monsters/brute-02.png');
    this.load.image('monster-brute-3', 'assets/monsters/brute-03.png');
    this.load.image('monster-brute-4', 'assets/monsters/brute-04.png');
    this.load.image('monster-shielded-1', 'assets/monsters/shielded-01.png');
    this.load.image('monster-shielded-2', 'assets/monsters/shielded-02.png');
    this.load.image('monster-shielded-3', 'assets/monsters/shielded-03.png');
    this.load.image('monster-bat-1', 'assets/monsters/bat-01.png');
    this.load.image('monster-bat-2', 'assets/monsters/bat-02.png');
    this.load.image('monster-flyer-1', 'assets/monsters/flyer-01.png');
    this.load.image('monster-flyer-2', 'assets/monsters/flyer-02.png');
    this.load.image('monster-flyer-3', 'assets/monsters/flyer-03.png');
    this.load.image('monster-juggernaut-1', 'assets/monsters/juggernaut-01.png');
    this.load.image('monster-juggernaut-2', 'assets/monsters/juggernaut-02.png');
    this.load.image('monster-juggernaut-3', 'assets/monsters/juggernaut-03.png');
    this.load.image('monster-juggernaut-4', 'assets/monsters/juggernaut-04.png');
    this.load.image('monster-swarm-1', 'assets/monsters/swarm-01.png');
    this.load.image('monster-swarm-2', 'assets/monsters/swarm-02.png');
    this.load.image('monster-swarm-3', 'assets/monsters/swarm-03.png');
    this.load.image('monster-phased-1', 'assets/monsters/phased-01.png');
    this.load.image('monster-phased-2', 'assets/monsters/phased-02.png');
    this.load.image('monster-phased-3', 'assets/monsters/phased-03.png');
    this.load.image('monster-phased-4', 'assets/monsters/phased-04.png');
    this.load.image('monster-phased-5', 'assets/monsters/phased-05.png');
    this.load.image('monster-phased-6', 'assets/monsters/phased-06.png');

    // Load boss images from assets/monsters/ (filenames: brute-boss-01.png, bronze-idol-01.png, etc.)
    for (let i = 1; i <= 7; i++) {
      const n = i < 10 ? `0${i}` : `${i}`;
      this.load.image(`boss-brute_boss-${i}`, `assets/monsters/brute-boss-${n}.png`);
    }
    for (let i = 1; i <= 10; i++) {
      const n = i < 10 ? `0${i}` : `${i}`;
      this.load.image(`boss-bronze_idol-${i}`, `assets/monsters/bronze-idol-${n}.png`);
    }
    for (let i = 1; i <= 9; i++) {
      const n = i < 10 ? `0${i}` : `${i}`;
      this.load.image(`boss-earth_tower-${i}`, `assets/monsters/earth-tower-${n}.png`);
    }
    for (let i = 1; i <= 6; i++) {
      const n = i < 10 ? `0${i}` : `${i}`;
      this.load.image(`boss-snail_beast-${i}`, `assets/monsters/snail-beast-${n}.png`);
    }
    // Giant bug: 4 animation sets (walking, walk_to_fly, flying, fly_to_walk)
    for (let i = 1; i <= 7; i++) {
      const n = i < 10 ? `0${i}` : `${i}`;
      this.load.image(`boss-giant_bug-walking-${i}`, `assets/monsters/bug-walking-${n}.png`);
    }
    for (let i = 1; i <= 3; i++) {
      const n = i < 10 ? `0${i}` : `${i}`;
      this.load.image(`boss-giant_bug-walk_to_fly-${i}`, `assets/monsters/bug-walk-to-fly-${n}.png`);
    }
    for (let i = 1; i <= 4; i++) {
      const n = i < 10 ? `0${i}` : `${i}`;
      this.load.image(`boss-giant_bug-flying-${i}`, `assets/monsters/bug-fly-${n}.png`);
    }
    for (let i = 1; i <= 5; i++) {
      const n = i < 10 ? `0${i}` : `${i}`;
      this.load.image(`boss-giant_bug-fly_to_walk-${i}`, `assets/monsters/bug-fly-to-walk-${n}.png`);
    }
  }

  create(): void {
    console.log('[PreloadScene] Assets loaded, starting menu...');
    SceneRouter.setCurrentScene(SCENES.PRELOAD);

    // Hidden asset preview: only when URL has ?preview=assets (works locally and on live site)
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    if (params?.get('preview') === 'assets') {
      SceneRouter.startAssetPreview(SCENES.PRELOAD);
      return;
    }

    SceneRouter.startMenu(SCENES.PRELOAD);
  }
}
